import React from "react";
import Dashboard from "./components/Dashboard";
class App extends React.Component {
  state = {
    city: undefined,
    state: undefined,
    country: undefined,
    temperature: undefined,
    timezoneFull: undefined,
    error: undefined,
    windSpeed: undefined,
    sunrise: undefined,
    sunset: undefined,
    date: undefined,
    forecast: undefined,
    hasSearched: false,
    weatherCode: undefined,
    pressure: undefined,
    visibility: undefined,
    humidity: undefined,
    hourlyTemperature: undefined,
    hourlyTime: undefined,
    temperatureUnit: 'fahrenheit',
    lastLatitude: undefined,
    lastLongitude: undefined,
    isLoading: false,
  };

  componentDidMount() {
    // Request user location on initial load (best-effort; if denied we keep the empty state message)
    if (!navigator?.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const latitude = pos?.coords?.latitude;
          const longitude = pos?.coords?.longitude;
          if (typeof latitude !== "number" || typeof longitude !== "number") return;

          // Reverse geocode to get a friendly location name (fallback to "Current Location")
          let locationData = null;
          try {
            const res = await fetch(
              `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${latitude}&longitude=${longitude}&count=1`
            );
            const data = await res.json();
            const best = data?.results?.[0];
            locationData = best
              ? best
              : { latitude, longitude, name: "Current Location", admin1: undefined, country_code: undefined };
          } catch {
            locationData = { latitude, longitude, name: "Current Location", admin1: undefined, country_code: undefined };
          }

          const syntheticEvent = { preventDefault: () => {}, target: { elements: {} } };
          this.getWeather(syntheticEvent, locationData);
        } catch {
          // Ignore location failures; keep empty-state message
        }
      },
      () => {
        // Permission denied / unavailable: do nothing (keep empty-state message)
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 10 * 60 * 1000 }
    );
  }

  // Helper method for geocoding queries (fallback when no location selected)
  getCoordinatesFromQuery = async (query) => {
    const res = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?` +
        `name=${encodeURIComponent(query)}` +
        `&count=1`
    );
    const data = await res.json();

    if (!data.results || data.results.length === 0) {
      throw new Error("Location not found");
    }

    return data.results[0];
  };

  getWeather = async (e, locationData = null) => {
    e.preventDefault();

    let latitude, longitude, city, state, country;

    if (locationData) {
      // Use provided location data from autocomplete selection
      latitude = locationData.latitude;
      longitude = locationData.longitude;
      city = locationData.name;
      state = locationData.admin1 || undefined;
      country = locationData.country_code || locationData.country;
    } else {
      // Fallback: try to geocode the input query
      const locationInput = e.target.elements.location?.value;
      if (!locationInput || locationInput.trim() === "") {
        this.setState({
          city: undefined,
          state: undefined,
          country: undefined,
          temperature: undefined,
          timezoneFull: undefined,
          windSpeed: undefined,
          hourlyTemperature: undefined,
          hourlyTime: undefined,
          error: "Please enter a location.",
          sunrise: undefined,
          sunset: undefined,
          isLoading: false,
        });
        return;
      }

      // Try to geocode the input
      try {
        const location = await this.getCoordinatesFromQuery(locationInput);
        latitude = location.latitude;
        longitude = location.longitude;
        city = location.name;
        state = location.admin1 || undefined;
        country = location.country_code || location.country;
      } catch (err) {
        this.setState({
          city: undefined,
          state: undefined,
          country: undefined,
          temperature: undefined,
          timezoneFull: undefined,
          windSpeed: undefined,
          hourlyTemperature: undefined,
          hourlyTime: undefined,
          error: "Location not found. Please try again.",
          sunrise: undefined,
          sunset: undefined,
          isLoading: false,
        });
        return;
      }
    }

    try {
      this.setState({ isLoading: true, error: "" });
      // Fetch main weather data
      const tempUnit = this.state.temperatureUnit === 'fahrenheit' ? 'fahrenheit' : 'celsius';
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true&temperature_unit=${tempUnit}&timezone=auto&daily=weathercode,temperature_2m_max,temperature_2m_min,sunrise,sunset,uv_index_max&hourly=temperature_2m,relativehumidity_2m,pressure_msl,visibility,uv_index`
      );
      const data = await response.json();

      const currentWeather = data.current_weather || {};
      const temperature = currentWeather.temperature;
      const weatherCode = currentWeather.weathercode;
      const hourly = data.hourly || {};
      const hourlyTemperature = hourly.temperature_2m || [];
      const hourlyTime = hourly.time || [];
      
      // Get current hour index for additional data
      const now = new Date();
      const timezoneFull = data.timezone;
      const currentHour = new Date(
        now.toLocaleString("en-US", { timeZone: timezoneFull })
      );
      currentHour.setMinutes(0, 0, 0);
      
      let currentHourIndex = 0;
      for (let i = 0; i < hourlyTime.length; i++) {
        const hourTime = new Date(hourlyTime[i]);
        if (hourTime >= currentHour) {
          currentHourIndex = i;
          break;
        }
      }
      
      // Extract current hour data
      const pressure = hourly.pressure_msl?.[currentHourIndex] != null 
        ? Math.round(hourly.pressure_msl[currentHourIndex]) 
        : null;
      const visibility = hourly.visibility?.[currentHourIndex] != null
        ? (hourly.visibility[currentHourIndex] / 1000).toFixed(1) // Convert meters to km
        : null;
      const humidity = hourly.relativehumidity_2m?.[currentHourIndex] != null
        ? Math.round(hourly.relativehumidity_2m[currentHourIndex])
        : null;

      // Find the current hour and filter to next 24 hours
      const getFilteredHourlyData = (
        times,
        temperatures,
        currentTime,
        timezoneFull
      ) => {
        if (!times.length || !temperatures.length)
          return { times: [], temperatures: [] };

        // Get current time in the location's timezone
        const now = new Date();
        const currentHour = new Date(
          now.toLocaleString("en-US", { timeZone: timezoneFull })
        );
        currentHour.setMinutes(0, 0, 0); // Round down to current hour

        // Find the index of the current hour or the next available hour
        let startIndex = 0;
        for (let i = 0; i < times.length; i++) {
          const hourTime = new Date(times[i]);
          if (hourTime >= currentHour) {
            startIndex = i;
            break;
          }
        }

        // Get next 24 hours
        const filteredTimes = times.slice(startIndex, startIndex + 24);
        const filteredTemperatures = temperatures.slice(
          startIndex,
          startIndex + 24
        );

        return { times: filteredTimes, temperatures: filteredTemperatures };
      };

      const {
        times: filteredHourlyTime,
        temperatures: filteredHourlyTemperature,
      } = getFilteredHourlyData(
        hourlyTime,
        hourlyTemperature,
        currentWeather.time,
        data.timezone
      );

      // Format current date
      const formatDate = (timezoneFull) => {
        const date = new Date();
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: timezoneFull,
        });
      };

      this.setState({
        city,
        state,
        country,
        timezoneFull: data.timezone,
        temperature,
        windSpeed:
          currentWeather.windspeed != null ? currentWeather.windspeed : null,
        hourlyTemperature: filteredHourlyTemperature,
        hourlyTime: filteredHourlyTime,
        error: "",
        sunrise: data.daily?.sunrise?.[0],
        sunset: data.daily?.sunset?.[0],
        date: formatDate(data.timezone),
        forecast: data.daily || null,
        hasSearched: true,
        weatherCode: weatherCode,
        pressure: pressure,
        visibility: visibility,
        humidity: humidity,
        lastLatitude: latitude,
        lastLongitude: longitude,
        isLoading: false,
      });
    } catch (err) {
      this.setState({
        city: undefined,
        state: undefined,
        country: undefined,
        temperature: undefined,
        timezoneFull: undefined,
        windSpeed: undefined,
        sunrise: undefined,
        sunset: undefined,
        date: undefined,
        forecast: undefined,
        error: err.message || "Unable to find that location.",
        hasSearched: true,
        isLoading: false,
      });
    }
  };

  setTemperatureUnit = (nextUnit) => {
    if (nextUnit !== 'fahrenheit' && nextUnit !== 'celsius') return;
    if (nextUnit === this.state.temperatureUnit) return;

    const toF = (c) => (c * 9) / 5 + 32;
    const toC = (f) => ((f - 32) * 5) / 9;
    const convertValue = (v, fromUnit, toUnit) => {
      if (v == null) return v;
      if (typeof v !== 'number') return v;
      if (fromUnit === toUnit) return v;
      if (fromUnit === 'celsius' && toUnit === 'fahrenheit') return toF(v);
      if (fromUnit === 'fahrenheit' && toUnit === 'celsius') return toC(v);
      return v;
    };
    const convertArray = (arr, fromUnit, toUnit) =>
      Array.isArray(arr) ? arr.map((v) => convertValue(v, fromUnit, toUnit)) : arr;

    this.setState((prev) => {
      const fromUnit = prev.temperatureUnit;
      const toUnit = nextUnit;

      const nextForecast =
        prev.forecast && typeof prev.forecast === 'object'
          ? {
              ...prev.forecast,
              temperature_2m_max: convertArray(prev.forecast.temperature_2m_max, fromUnit, toUnit),
              temperature_2m_min: convertArray(prev.forecast.temperature_2m_min, fromUnit, toUnit),
            }
          : prev.forecast;

      return {
        temperatureUnit: nextUnit,
        temperature: convertValue(prev.temperature, fromUnit, toUnit),
        hourlyTemperature: convertArray(prev.hourlyTemperature, fromUnit, toUnit),
        forecast: nextForecast,
      };
    });
  };

  render() {
    return (
      <Dashboard 
        weatherData={this.state} 
        onSearch={this.getWeather}
        temperatureUnit={this.state.temperatureUnit}
        onSetTemperatureUnit={this.setTemperatureUnit}
      />
    );
  }
}

export default App;
